import React from 'react'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { errorTypes } from '@/utility/validation'
import { useFormContext } from 'react-hook-form'
import type { OnboardTabProps } from './onboard.types'
import states from '@/utility/states.json'

const fetchPincodeDetails = async (pin: string) => {
    if (!pin || pin.length !== 6 || !/^\d{6}$/.test(pin)) return null;
    try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
            const postOffice = data[0].PostOffice[0];
            return {
                district: postOffice.District,
                state: postOffice.State,
            };
        }
    } catch (e) {
        console.error("Error fetching pincode details:", e);
    }
    return null;
};

const OnboardAddress: React.FC<OnboardTabProps> = ({ isLoading, handleNextStep }) => {
    const methods = useFormContext()
    const { register, watch, setValue, formState: { errors } } = methods

    const isSameAsPermanentAddress = watch('isSameAsPermanentAddress')
    const presentAddress = watch('address')
    const presentState = watch('state')
    const presentCity = watch('city')
    const presentPinCode = watch('pinCode')
    const secondaryAddress = watch('secondaryAddress')
    const permanentPinCode = watch('permanentPinCode')

    // Fetch and auto-fill State and District/City when Current Pincode is entered
    React.useEffect(() => {
        if (presentPinCode && presentPinCode.length === 6 && /^\d{6}$/.test(presentPinCode)) {
            const getDetails = async () => {
                const details = await fetchPincodeDetails(presentPinCode);
                if (details) {
                    if (details.state) {
                        setValue('state', details.state, { shouldValidate: true, shouldDirty: true });
                    }
                    if (details.district) {
                        setValue('city', details.district, { shouldValidate: true, shouldDirty: true });
                    }
                }
            };
            getDetails();
        }
    }, [presentPinCode, setValue]);

    // Fetch and auto-fill Permanent State and District/City when Permanent Pincode is entered
    React.useEffect(() => {
        if (!isSameAsPermanentAddress && permanentPinCode && permanentPinCode.length === 6 && /^\d{6}$/.test(permanentPinCode)) {
            const getPermanentDetails = async () => {
                const details = await fetchPincodeDetails(permanentPinCode);
                if (details) {
                    if (details.state) {
                        setValue('permanentState', details.state, { shouldValidate: true, shouldDirty: true });
                    }
                    if (details.district) {
                        setValue('permanentCity', details.district, { shouldValidate: true, shouldDirty: true });
                    }
                }
            };
            getPermanentDetails();
        }
    }, [permanentPinCode, isSameAsPermanentAddress, setValue]);

    // Automatically sync Permanent Address fields with Present Address when checked
    React.useEffect(() => {
        if (isSameAsPermanentAddress) {
            setValue('permanentAddress', presentAddress || '')
            setValue('permanentState', presentState || '')
            setValue('permanentCity', presentCity || '')
            setValue('permanentPinCode', presentPinCode || '')
            setValue('secondaryPermanentAddress', secondaryAddress || '')
        }
    }, [isSameAsPermanentAddress, presentAddress, presentState, presentCity, presentPinCode, secondaryAddress, setValue])

    return (
        <>
            <div className="my-3 border-b border-gray-200 pb-3 flex items-center flex-wrap justify-between">
                <h4 className="text-lg font-semibold">
                    Current Address
                </h4>

            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    {...register('address', {
                        ...errorTypes.required('Address'),
                        ...errorTypes.address,
                    })}
                    error={errors.address?.message as string}
                    label=" Full Address"
                    id="Address"
                    placeholder="Enter Full Address"
                    defaultValue={""}
                    required
                    maxLength={150}
                />
                <FormField
                    {...register('secondaryAddress', {
                        ...errorTypes.address,
                        // ...errorTypes.required('Secondary Address'),
                    })}
                    error={errors.secondaryAddress?.message as string}
                    label=" Secondary Address (Optional)"
                    id="secondaryAddress"
                    placeholder="Enter Secondary Address"
                    defaultValue={""}
                    maxLength={150}
                // required
                />
                <FormField
                    {...register('pinCode', {
                        ...errorTypes.required('Pin code'),
                        ...errorTypes.pinCode,
                    })}
                    error={errors.pinCode?.message as string}
                    label="Pin Code (Pincode Auto Fetch State & City)"
                    placeholder="Enter pin code"
                    type="text"
                    maxLength={6}
                    required
                />
                <FormField
                    {...register('state', {
                        ...errorTypes.required('State')
                    })}
                    error={errors.state?.message as string}
                    type="select"
                    label="State"
                    value={watch('state')}
                    placeholder="Enter state"
                    options={states}
                    required
                />
                <FormField
                    {...register('city', {
                        ...errorTypes.required('City'),
                        ...errorTypes.city,
                    })}
                    error={errors.city?.message as string}
                    label="City"
                    id="city"
                    placeholder="Enter city"
                    required
                    maxLength={50}
                />
            </div>
            <div className="my-3 border-b border-gray-200 pb-3 flex items-center flex-wrap justify-between">
                <h4 className="text-lg font-semibold">
                    Permanent Address
                </h4>
                <div className="form-check">
                    <FormField
                        {...register('isSameAsPermanentAddress')}
                        type="checkbox"
                        label="Same as Current address"
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    {...register('permanentAddress', {
                        ...errorTypes.required('Address'),
                        ...errorTypes.address,
                    })}
                    error={errors.permanentAddress?.message as string}
                    label="Address"
                    id="Address"
                    placeholder="Enter address"
                    defaultValue={""}
                    required
                    maxLength={150}
                    disabled={isSameAsPermanentAddress}
                />
                <FormField
                    {...register('secondaryPermanentAddress', {
                        // ...errorTypes.required('Secondary Address'),
                    })}
                    error={errors.secondaryPermanentAddress?.message as string}
                    label=" Secondary Address (Optional)"
                    id="secondaryPermanentAddress"
                    placeholder="Enter Secondary Address"
                    defaultValue={""}
                    maxLength={150}
                    disabled={isSameAsPermanentAddress}
                // required
                />
                <FormField
                    {...register('permanentPinCode', {
                        ...errorTypes.required('Pin code'),
                        ...errorTypes.pinCode,
                    })}
                    error={errors.permanentPinCode?.message as string}
                    label="Pin Code (Pincode Auto Fetch State & City)"
                    id="Pincode"
                    placeholder="Enter pin code"
                    maxLength={6}
                    type="text"
                    disabled={isSameAsPermanentAddress}
                    required
                />
                <FormField
                    {...register('permanentState', {
                        ...errorTypes.required('State'),
                    })}
                    error={errors.permanentState?.message as string}
                    type="select"
                    label="State"
                    value={watch('permanentState')}
                    placeholder="Enter state"
                    options={states}
                    required
                    disabled={isSameAsPermanentAddress}
                />
                <FormField
                    {...register('permanentCity', {
                        ...errorTypes.required('City'),
                        ...errorTypes.city,
                    })}
                    error={errors.permanentCity?.message as string}
                    label="City"
                    id="City"
                    placeholder="Enter city"
                    disabled={isSameAsPermanentAddress}
                    maxLength={50}
                    required
                />
            </div>
            <div className="flex items-center justify-end mt-6">
                <Button
                    type="button"
                    onClick={() => handleNextStep("address", 'tab6', [
                        'address',
                        'state',
                        'city',
                        'pinCode',
                        'secondaryAddress',
                        'isSameAsPermanentAddress',
                        'permanentAddress',
                        'permanentState',
                        'permanentCity',
                        'permanentPinCode',
                        'secondaryPermanentAddress',
                    ])}
                >
                    {isLoading ? (
                        <>
                            <Spinner />
                        </>
                    ) : (
                        'Save and Continue'
                    )}
                </Button>
            </div>
        </>
    )
}

export default OnboardAddress
